import React, { useEffect } from 'react';
import { getEnv } from 'mobx-state-tree';
import { Observer } from 'mobx-react';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';

import { Avatar } from '@shared/components/Avatar';
import { Table } from '@shared/components/Table';
import { NoResultsView } from '@shared/components/NoResultsView';
import EditIcon from '@assets/edit.svg';
import NeoIcon from '@assets/competencies-icon.svg';

import {
    useUsersGridUIStore,
    useUserUIStore,
    useUserPermissionsUIStore,
    useUserProfileUIStore,
} from '@core/useStores';
import { IStoresEnv } from '@core/storesEnv';
import { useStyles } from './UsersList.styles'
import { UsersFiltersBar } from '../UsersFiltersBar';
import { NoUsersInGroup } from '../NoUsersInGroup';
import { UserModal } from '../UserModal';
import { ResendInviteModal } from '../ResendInviteModal';
import { UserRole } from '../../domain/UserRole';
import { ConfirmationModal } from '@shared/components/ConfirmationModal';
import { ToastMessage } from '@shared/components/Toast';

import { RoutePaths } from '@core/routes/RoutePaths';
import { useNavigate } from 'react-router-dom';

import { messages } from 'Users/users.messages';
import {
    IPageQueryStoreSnapshotIn,
    IUserStore,
} from 'Users/stores';
import { ThemeProps } from '@styles/theme';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Box, Tooltip, Typography } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

type FetchDataType = {
    pageIndex: number;
    sortBy: {
        id: string;
        desc: boolean;
    }[];
};

const NoInfo = () => {
    const styles = useGridStyles();

    return <div className={styles.noInfo}>{messages['usersPage.noInfo']}</div>;
};

const columnHelper = createColumnHelper<IUserStore>();

const getColumnsData = (options: { compact: boolean; mobile: boolean }) => [
    columnHelper.accessor('fullName', {
        header: messages['usersPage.table.fullName.header'],
        meta: { cardRole: 'title' },
        size: options.mobile ? 36 : options.compact ? 24 : 20,
        cell: ({ cell: { row }, getValue }) => {
            const styles = useGridStyles();
            const { firstName, lastName, avatar } = row.original as {
                firstName: string;
                lastName: string;
                avatar: string;
            };

            return (
                <div className={styles.userWrapper}>
                    <Avatar
                        firstName={firstName}
                        lastName={lastName}
                        image={avatar}
                        className={styles.userAvatar}
                    />
                    <span className={styles.ellipsis}>{getValue()}</span>
                </div>
            );
        },
    }),
    columnHelper.accessor('roles', {
        header: messages['usersPage.table.roles.header'],
        meta: { cardRole: 'badge' },
        size: options.mobile ? 16 : 10,
        cell: ({ cell }) => {
            const styles = useGridStyles();
            const value = cell.getValue();

            return value.length ? (
                <span className={styles.capitalized}>
                    {value[0].name.toLowerCase()}
                </span>
            ) : (
                <NoInfo />
            );
        },
    }),
    columnHelper.accessor('department', {
        header: messages['usersPage.table.department.header'],
        meta: { cardRole: 'detail', hideCardWhenEmpty: true },
        size: options.compact ? 16 : 20,
        // disableEllipsis: true,
        cell: ({ getValue }) => <span title={getValue()}>{getValue()}</span>,
    }),
    columnHelper.accessor('groups', {
        header: messages['usersPage.table.groups.header'],
        meta: { cardRole: 'detail' },
        size: options.mobile ? 26 : options.compact ? 22 : 20,
        // cell: ({ getValue, cell: { row } }) => {
        cell: ({ getValue }) => {
            const styles = useGridStyles();
            const value = getValue();
            const groups = Array.from(value).sort((a, b) => a.name.localeCompare(b.name));
            const firstGroup = groups.length && groups[0];

            const getGroups = (groups: {id: string, name:string}[]) => {
                return (
                    <Box>
                        {groups.map(v => {
                            if(groups.indexOf(v) > 0){
                                return(
                                    <Typography key={v.id} variant='body2' style={{ fontSize: 12}}>
                                        {v.name}
                                    </Typography>
                                );
                            }else{
                                return <></>
                            }
                        })}
                    </Box>
                );
            }

            if (!firstGroup) {
                return <NoInfo />;
            }

            return (
                <div className={styles.groupsContainer}>
                    <div className={styles.firstGroup}>
                        {firstGroup.name}
                    </div>
                    {groups.length > 1 && (
                        <Tooltip title={getGroups(groups)} arrow enterDelay={1000}>
                            <div
                                className={styles.groupsCounter}
                            >
                                <span className={styles.moreItemsPresent}>
                                    +{groups.length - 1}
                                </span>
                            </div>
                        </Tooltip>
                    )}
                </div>
            );
        },
    }),
    columnHelper.accessor('email', {
        header: messages['usersPage.table.email.header'],
        meta: { cardRole: 'subtitle' },
        size: 16,
        cell: ({ getValue }) => {
            const value = getValue();

            if (!value) {
                return <NoInfo />;
            }

            return <>{value}</>;
        },
    }),
    columnHelper.accessor('procedures', {
        header: messages['usersPage.table.assignedProcedures.header'],
        meta: { cardRole: 'detail' },
        size: options.compact ? 18 : 20,
        cell: ({ getValue }) => {
            const styles = useGridStyles();
            const value = getValue();
            const procedures = Array.from(value).sort((a, b) => a.name.localeCompare(b.name));
            const firstProcedure = procedures.length && procedures[0];

            const getProcedures = (procedures: {id: string, name:string}[]) => {
                return (
                    <Box>
                        {procedures.map(v => {
                            if(procedures.indexOf(v) > 0){
                                return(
                                    <Typography key={v.id} variant='body2' style={{ fontSize: 12}}>
                                        {v.name}
                                    </Typography>
                                );
                            }else{
                                return <></>
                            }
                        })}
                    </Box>
                );
            }

            if (!firstProcedure) {
                return <NoInfo />;
            }

            return (
                <div className={styles.proceduresContainer}>
                    <div className={styles.firstProcedure}
                        style={{
                            width: procedures.length > 1 ? '75%' : '100%',
                        }}>
                        <span className={styles.firstProcedureLabel}>
                            {firstProcedure.name}
                        </span>
                    </div>
                    {procedures.length > 1 && (
                        <Tooltip title={getProcedures(procedures)} arrow enterDelay={1000}>
                            <div
                                className={styles.proceduresCounter}
                            >
                                <span className={styles.moreItemsPresent}>
                                    +{procedures.length - 1}
                                </span>
                            </div>
                        </Tooltip>
                    )}
                </div>
            );
        },
    }),
    columnHelper.display({
        id: 'edit',
        meta: { cardRole: 'actions' },
        header: () => {
            return <></>;
        },
        minSize: options.mobile ? 132 : 152,
        maxSize: options.mobile ? 152 : 168,
        size: options.mobile ? 26 : options.compact ? 18 : 14,
        enableSorting: false,
        cell: ({ cell }) => {
            const styles = useGridStyles();
            const row = cell.row;
            const userUIStore = useUserUIStore();
            const userId = useUserProfileUIStore().userInfo.id;
            const userPermissionsUIStore = useUserPermissionsUIStore();
            const navigate = useNavigate();

            const original: Record<string, any> = row.original;
            const userRoles = original.roles.map((role: UserRole) => role.name);

            const canEdit =
                original.id === userId ||
                userPermissionsUIStore.canEditUser(userRoles);
            const canResendInvitation =
                userPermissionsUIStore.canUpdateUser &&
                canEdit &&
                Boolean(original.email) &&
                /invite/i.test(original.status?.name ?? '');

            const onCellClick = () => {
                userUIStore.setEditUserId(original.id);
                userUIStore.toggleUserModalOpen(true);
            };

            const onUserClick = () => {
                navigate(`/${RoutePaths.users}/analytics/${original.id}`)
            };

            const onResendInviteClick = () => {
                userUIStore.setResendInvitationInputData({
                    id: original.id,
                    email: original.email,
                    fullName: original.fullName,
                });
                userUIStore.toggleResendInvitationModalOpened(true);
            };

            return (
                <div className={styles.userActions}>
                    <button
                        onClick={onUserClick}
                        className={styles.button}
                        title={messages['usersPage.table.goToAnalytics']}
                    >
                        <img src={NeoIcon} style={{
                            width: 24,
                            height: 24,
                        }}/>
                    </button>
                    {canResendInvitation && (
                        <button
                            onClick={onResendInviteClick}
                            className={styles.button}
                            title={messages['usersPage.table.resendInvitation']}
                        >
                            <MailOutlineIcon color="primary" />
                        </button>
                    )}
                    <button
                        onClick={() => canEdit && onCellClick()}
                        disabled={!canEdit}
                        className={styles.button}
                        title={messages['usersPage.table.editUser']}
                    >
                        <img src={EditIcon} />
                    </button>
                </div>
            );
        },
    }),
];

const useGridStyles = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    return styles;
};

export const UsersList: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isMobileLayout = useBreakpointDown('mobile');
    const isCompactLayout = useBreakpointDown('desktop');
    const isNarrowLayout = useBreakpointDown('desktop');

    const usersGridUIStore = useUsersGridUIStore();
    const userUIStore = useUserUIStore();

    const { filtersOptions } = getEnv<IStoresEnv>(usersGridUIStore);

    const initialSortState = {
        sorting: [
            {
                id: 'fullName',
                desc: false,
            },
        ],
    };

    const columns: ColumnDef<IUserStore, any>[] = React.useMemo(() => {
        const baseColumns = getColumnsData({
            compact: isCompactLayout,
            mobile: isMobileLayout,
        });

        // Below 'mobile' the Table renders cards, which lay out every column
        // via its cardRole -- no need to drop columns there.
        if (isMobileLayout || !isCompactLayout) {
            return baseColumns;
        }

        const hiddenColumns = isNarrowLayout
            ? ['email', 'department', 'groups', 'procedures']
            : ['email', 'department', 'procedures'];

        return baseColumns.filter((column: any) => !hiddenColumns.includes(column.id ?? column.accessorKey));
    }, [isCompactLayout, isMobileLayout, isNarrowLayout]);

    const setGroupFilter = (inGroup: number) => {
        usersGridUIStore.setParams({
            page: 0,
            inGroup,
            inGroupId: undefined,
        });

        //groups.unselectGroup();
    }

    useEffect(() => {
        setGroupFilter(0);

        usersGridUIStore.cleanUp();

        usersGridUIStore.load();
        usersGridUIStore.loadStatistics();

        filtersOptions.loadFilterOptions();
        setTimeout(() => {
            usersGridUIStore.togglePageActivity(true);
        }, 100);

        return () => {
            usersGridUIStore.togglePageActivity(false);
            usersGridUIStore.cleanUp();
        };
    }, []);

    const onFetchData = ({ pageIndex, sortBy }: FetchDataType) => {
        const params: IPageQueryStoreSnapshotIn = { page: pageIndex };

        if (sortBy && sortBy.length) {
            params.sortFieldName = sortBy[0].id;
            params.sortDirection = Number(sortBy[0].desc);
        }
        if (usersGridUIStore.isActivePage) {
            usersGridUIStore.setParams(params);
        }
    };

    const onDeleteUserDecline = () => {
        userUIStore.toggleDeleteConfirmationModalOpened(false);
        userUIStore.toggleUserModalOpen(false);
        userUIStore.clearEditUserId();
    };

    const onDeleteUserConfirm = async () => {
        const result = await userUIStore.deleteUser({
            id: userUIStore.userToEditId,
        });

        if (!result.success) {
            toast.error(
                <ToastMessage
                    message={messages['usersPage.removeUser.toast.error']}
                    type="error"
                />
            );
        } else {
            toast.success(
                <ToastMessage
                    message={messages['usersPage.removeUser.toast.success']}
                    type="success"
                />
            );

            usersGridUIStore.setParams({ page: 0 });
            usersGridUIStore.load();
            usersGridUIStore.loadStatistics();
            filtersOptions.loadFilterOptions();
        }

        userUIStore.clearEditUserId();
        userUIStore.toggleUserModalOpen(false);
        userUIStore.toggleDeleteConfirmationModalOpened(false);
    };

    const onInviteUserModalClose = () => {
        const params = usersGridUIStore.inviteUser.getInviteUserFields;

        usersGridUIStore.inviteUser.setInviteUserFields({
            ...params,
            errors: null,
        });
        userUIStore.clearEditUserId();
        userUIStore.toggleUserModalOpen(false);
    };

    const onResendInviteUserModalClose = () => {
        userUIStore.setResendInvitationInputData(null);
        userUIStore.toggleResendInvitationModalOpened(false);
    };

    return (
        <>
            <UsersFiltersBar />
            <Observer>
                {() => {
                    const data = usersGridUIStore.users.toJSON();
                    const preparedData = data.map(row => ({
                        ...row,
                    }));

                    const genericColumns = columns as ColumnDef<{}, any>[];

                    return (
                        <>
                            {
                                userUIStore.userModalOpened &&
                                <UserModal
                                    isOpen={userUIStore.userModalOpened}
                                    onRequestClose={onInviteUserModalClose}
                                />
                            }
                            <ResendInviteModal
                                isOpen={userUIStore.resendInvitationModalOpened}
                                onRequestClose={onResendInviteUserModalClose}
                            />
                            {/* TODO move logic of modals to separate component */}
                            <ConfirmationModal
                                isOpen={
                                    userUIStore.deleteConfirmationModalOpened
                                }
                                message={messages['usersPage.delete.message']}
                                confirmText={
                                    messages['usersPage.delete.confirmText']
                                }
                                onDecline={onDeleteUserDecline}
                                onConfirm={onDeleteUserConfirm}
                            />

                            {usersGridUIStore.isTableVisible && (
                                <div className={styles.tableContainer}>
                                    <Table
                                        data={preparedData}
                                        columns={genericColumns}
                                        fetchData={onFetchData}
                                        isLoading={
                                            usersGridUIStore.status.isLoading
                                        }
                                        useSkeleton={true}
                                        paginationStatus={{
                                            ...usersGridUIStore.pagination,
                                        }}
                                        initialState={initialSortState}
                                    />
                                </div>
                            )}

                            {usersGridUIStore.noFilteringResults && (
                                <NoResultsView entityName="users" />
                            )}

                            {usersGridUIStore.noItemsInGroup && (
                                <NoUsersInGroup />
                            )}
                        </>
                    );
                }}
            </Observer>
        </>
    );
};
